import { cineamoApiClient } from '../../apiClient';
import { RequestConfig } from '../../fetch-helper';
import { UrlQueryParameter, buildUrlQuery } from '../../helpers/url-query-helper';
import { ContentDto } from '../../models/content/ContentDto.types';
import { MovieTmdbImagesDto } from '../../models/movie/MovieTmdbImagesDto.types';
import { ReleaseDateType } from '../../models/movie/ReleaseDateType.types';
import { MovieDto } from '../../models/movie/v1/MovieDto.types';
import { MovieDto as MovieV2Dto } from '../../models/movie/v2/MovieDto.types';
import { MovieAgeRatingDto, MovieAgeRatingInputDto } from '../../models/movie-age-rating/MovieAgeRatingDto.types';
import { TrailerCategory } from '../../models/movie-trailer/TrailerCategory.enum';
import { ApiErrorResponse } from '../../types/ApiErrorResponse.types';
import {
    MovieListResponse,
    MovieAgeRatingListResponse,
    MovieTrailerListResponse
} from '../../types/ApiListResponse.types';
import { ApiResponse } from '../../types/ApiResponse.types';
import { CountryCode } from '../../types/CountryCode';

// <------------------------ Movies ------------------------>

/**
 * The numeric movie id these endpoints address a movie by. Non-nullable: it is the identity-
 * generated primary key, so every movie response carries it (CIN-13072 closed the last gap, where
 * V2's `MovieLocalizedDto` still declared `?int $id` after CIN-14670 had fixed only V1).
 */
type MovieId = MovieV2Dto['id'];

type GetMoviesQueryParameter = UrlQueryParameter & {
    releaseDateStart?: string;
    releaseDateEnd?: string;
    region?: string;
    distributorId?: string;
    isShowingNow?: boolean;
    cinemaCountryCode?: CountryCode;
};

/**
 * This endpoint returns a list of movies.
 *
 * @deprecated Use `getMovies` from `@cineamo/api/resources/movie/movie-v2-api` instead.
 *             The V2 endpoint is locale-aware and cinema-customizable.
 *
 * @example
 * // Get a list of movies:
 * getMovies({...urlParams})
 *
 * @param urlParams
 * @returns {ApiResponse<MovieListResponse>}
 */

export async function getMovies(urlParams?: GetMoviesQueryParameter): Promise<ApiResponse<MovieListResponse> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieListResponse>>(`/movies${buildUrlQuery(urlParams)}`)
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Movie ------------------------>

/**
 * This endpoint returns a movie.
 *
 * @deprecated Use `getMovieById` from `@cineamo/api/resources/movie/movie-v2-api` instead.
 *             The V2 endpoint is locale-aware and cinema-customizable.
 *
 * @example
 * // Get a movie by id:
 * getMovies(1)
 *
 * @param movieId
 * @param config
 * @returns {ApiResponse<MovieDto>}
 */

export async function getMovieById(
    // Resolves three forms: a numeric Movie Id, a slug, and a legacy `<tmdbId>m` Cineamo Id. Being
    // the only movie lookup that still takes the third is the reason this deprecated endpoint is
    // called at all — it resolves references arriving from outside the frontend, where we do not
    // control the id space: an old inbound link (redirectMovieSlugMiddleware) or an embed's
    // `?movie=` param (the showtimes-finder widget). Both read `id` off the response and pass that
    // numeric Movie Id on, so the legacy space stops here and never reaches a second request.
    //
    // The legacy form is not spelled as `${MovieDto['tmdbId']}m` because `MovieDto['slug']` is
    // already `string` and would absorb it, making the union redundant rather than more precise.
    movieId: MovieId | MovieDto['slug'],
    config?: RequestConfig
): Promise<ApiResponse<MovieDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<MovieDto>>(`/movies/${movieId}`, {
            ...(config ?? {}),
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

export async function getMovieContent(
    movieId: MovieId,
    config?: RequestConfig
): Promise<ApiResponse<ContentDto> | null> {
    return cineamoApiClient
        .get<ApiResponse<ContentDto>>(`/movies/${movieId}/content`, {
            ...(config ?? {}),
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * Returns the complete TMDB artwork inventory of a movie (admin only, CIN-14129).
 * Unlike the public movie endpoints, globally disabled images are included here
 * (flagged via isDisabled) so an admin can re-enable them.
 *
 * `appLocaleKeys` names the locales to report `servedForLocaleKeys` and
 * `candidateForLocaleKeys` for (CIN-14885). Which locales an app serves is the app's
 * own fact, so it is sent rather than mirrored server-side; omit it and both arrays
 * come back empty.
 *
 * @param movieId
 * @param appLocaleKeys
 */
export async function getMovieTmdbImages(
    movieId: MovieId,
    appLocaleKeys: readonly string[] = []
): Promise<MovieTmdbImagesDto | null> {
    const query = appLocaleKeys.length > 0 ? `?localeKeys=${encodeURIComponent(appLocaleKeys.join(','))}` : '';
    return cineamoApiClient
        .get<MovieTmdbImagesDto>(`/movies/${movieId}/tmdb-images${query}`, {
            credentials: 'same-origin'
        })
        .then((response) => response.data)
        .catch(() => {
            return null;
        });
}

/**
 * This endpoint syncs Movie with TMDB.
 *
 * Keyed on the **TMDB id**, not the internal `Movie.id`: the handler resolves the movie by building
 * a Cineamo Id out of the path segment, so that segment is a tmdbId despite the route naming it
 * `movieId`. Nothing to migrate here — no movie id, in either space, is a valid argument.
 *
 * @param tmdbId
 */
export async function fetchTmdbMovie(tmdbId: MovieDto['tmdbId']): Promise<boolean> {
    return cineamoApiClient
        .post(`/movies/${tmdbId}/fetch-tmdb`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Movie Age Ratings ------------------------>

type GetMovieAgeRatingsQueryParameter = UrlQueryParameter & {
    ageRatingIds?: number[];
    iso31662s?: string[];
};

export function getMovieAgeRatings(
    movieId: MovieId,
    params?: GetMovieAgeRatingsQueryParameter
): Promise<ApiResponse<MovieAgeRatingListResponse> | null> {
    if (!movieId) {
        return Promise.resolve(null);
    }
    return cineamoApiClient
        .get<ApiResponse<MovieAgeRatingListResponse>>(`/movies/${movieId}/age-ratings` + buildUrlQuery(params), {
            credentials: 'same-origin'
        })
        .then((res) => res.data)
        .catch(() => {
            return null;
        });
}

export function setMovieAgeRating(
    movieId: MovieId,
    movieAgeRating: MovieAgeRatingInputDto
): Promise<ApiResponse<MovieAgeRatingDto> | ApiErrorResponse<MovieAgeRatingInputDto>> {
    return cineamoApiClient
        .put(`/movies/${movieId}/age-ratings`, movieAgeRating)
        .then((res) => res.data)
        .catch((error) => error);
}
// <------------------------ Movie Trailers ------------------------>

type GetMovieTrailersQueryParameter = UrlQueryParameter & {
    movieId?: number;
    categories?: TrailerCategory[];
};

/**
 * This endpoint returns a list of movie trailers (from KinoCheck).
 *
 * @example
 * // Get the movie trailers for a specific movie:
 * getMovieTrailers(123);
 *
 * @param movieId
 * @param params
 * @returns {ApiResponse<MovieTrailerListResponse>}
 */
export function getMovieTrailers(
    movieId: number,
    params?: GetMovieTrailersQueryParameter
): Promise<ApiResponse<MovieTrailerListResponse> | null> {
    if (!movieId) {
        return Promise.resolve(null);
    }
    return cineamoApiClient
        .get<ApiResponse<MovieTrailerListResponse>>(`/movies/${movieId}/trailers` + buildUrlQuery(params))
        .then((res) => res.data)
        .catch(() => {
            return null;
        });
}

// <------------------------ Delete Movie ------------------------>

/**
 * Soft-delete a movie.
 *
 * @param movieId - The internal id of the movie to delete
 * @returns {Promise<boolean>} - True if successful, false otherwise
 */
export async function deleteMovie(movieId: MovieId): Promise<boolean> {
    return cineamoApiClient
        .delete(`/movies/${movieId}`)
        .then((response) => response.status === 204)
        .catch(() => false);
}

// <------------------------ Patch Movie ------------------------>

/**
 * TMDB provenance on a credit row: the person's ids and their photo.
 *
 * Nothing in the admin UI sets these — there is no truthful value to type for a person TMDB does
 * not know. They exist so a row a clone inherited survives being sent back: cast and crew replace
 * wholesale, so a row that omits them loses them, and `profilePath` is the actor photo.
 */
type MovieCreditProvenance = {
    id?: number;
    gender?: number;
    creditId?: string;
    profilePath?: string;
};

/** One billed performer of a movie of ours (CIN-14766). */
export type MovieCastInput = MovieCreditProvenance & {
    name: string;
    character?: string | null;
    /** Billing order. Omitted sorts last, not first — the generated cast list defaults it to INT_MAX. */
    order?: number;
    castId?: number;
};

/** One crew member of a movie of ours (CIN-14766). `job` drives the director facet of search. */
export type MovieCrewInput = MovieCreditProvenance & {
    name: string;
    job: string;
    department?: string | null;
};

/**
 * One locale's words for a movie of ours (CIN-15191).
 *
 * The collection MERGES on the `(iso6391, iso31661)` pair: a locale that is sent replaces its own
 * texts in full — so an emptied field actually clears — and a locale that is not sent is left
 * alone. That asymmetry with `cast`/`crew` is deliberate: a film carries 30-60 TMDB translations
 * and no editor renders them all, so a wholesale replace would delete every locale it skipped.
 * A locale whose fields are all empty is removed from the collection.
 *
 * `iso6391` is lowercase, `iso31661` uppercase — the shape every matcher downstream compares.
 */
export type MovieTranslationInput = {
    iso6391: string;
    iso31661: string;
    title?: string | null;
    overview?: string | null;
    tagline?: string | null;
    homepage?: string | null;
    runtime?: number | null;
};

/**
 * One release of a movie of ours, inside its country's entry of {@link MovieReleaseDatesInput}.
 *
 * `releaseDate` is `YYYY-MM-DD`; the API stores TMDB's full ISO datetime. `certification` is absent
 * on purpose — the age rating is curated through the movie's age ratings and carried across by the
 * API, so it is not this payload's to set.
 */
export type MovieReleaseDateInput = {
    releaseDate: string;
    type: ReleaseDateType;
    note?: string | null;
    iso6391?: string | null;
};

/**
 * Per-country release dates of a movie of ours (CIN-15191), as a JSON-merge-patch map of uppercase
 * ISO 3166-1 country code to that country's entries — the same semantics as `primaryPosterPaths`.
 *
 * A country that is sent has its whole list replaced; `null` (or an empty list) drops it; a country
 * that is NOT sent is left exactly as it is; a top-level `null` clears the collection.
 *
 * Merge, not replace, because the collection dwarfs anything an editor shows: The Batman carries 67
 * countries and 84 dated rows, and an editor that renders the handful Cineamo serves would delete
 * the rest on its first save.
 */
export type MovieReleaseDatesInput = Record<string, MovieReleaseDateInput[] | null>;

export type MovieDtoInput = {
    /**
     * Catalog data of a movie of ours, editable only on a film cloned from another (CIN-14766).
     * Sending any of the eight for a TMDB-synced movie is a 422: the next import replaces the
     * whole document these are stored in, so the edit would silently disappear.
     *
     * `title` null clears it and the displayed title falls back to `originalTitle`, which is why
     * that one cannot be emptied. `releaseDate` must be `YYYY-MM-DD` exactly. `cast` and `crew`
     * replace wholesale, so an empty array clears them.
     */
    title?: string | null;
    originalTitle?: string;
    releaseDate?: string | null;
    overview?: string | null;
    tagline?: string | null;
    runtime?: number | null;
    cast?: MovieCastInput[];
    crew?: MovieCrewInput[];
    /** Merges per locale — see MovieTranslationInput. */
    translations?: MovieTranslationInput[];
    /** Merges per country — see MovieReleaseDatesInput. */
    releaseDates?: MovieReleaseDatesInput | null;
    isTmdbSyncSkipped?: boolean;
    rentrakTitle?: number | null;
    isPosterHidden?: boolean;
    isBackdropHidden?: boolean;
    isLogoHidden?: boolean;
    /**
     * Admin-chosen primary poster per locale key ('de', 'de-CH', 'xx' = language-less posters),
     * values are TMDB file paths or 'upload:<uuid>' refs; merge-patch semantics: sent keys are
     * merged, a null value removes that locale's choice, top-level null clears the whole map
     */
    primaryPosterPaths?: Record<string, string | null> | null;
    /**
     * Admin-chosen primary backdrop per locale key, same semantics as primaryPosterPaths;
     * the 'xx' entry effectively applies to every language (language-less backdrops top
     * the serving ladder)
     */
    primaryBackdropPaths?: Record<string, string | null> | null;
    /** Admin-chosen primary logo per locale key, same semantics as primaryPosterPaths */
    primaryLogoPaths?: Record<string, string | null> | null;
};

/** One of a movie's custom image uploads (CIN-14535). */
export type MovieImageUploadDto = {
    /**
     * The upload's identity, everywhere. Delete it by this, and write
     * `upload:<assetRef>` into a primary map to make it a locale's pick — which is what
     * `primaryValue` already spells out.
     */
    assetRef: string;
    type: 'poster' | 'backdrop' | 'logo';
    primaryValue: string;
    assetUrl: string;
    /** Null for uploads backfilled from the superseded single-ref columns. */
    createdAt: string | null;
    /**
     * The locale keys whose primary pick names this upload — the locales it is actually served to.
     * Empty means it is stored and shown to nobody, which is the state most worth surfacing: an
     * upload does nothing until it is picked. Reported here because the picks live in the movie's
     * per-locale maps, which the movie endpoints do not publish.
     */
    primaryForLocaleKeys: string[];
};

/**
 * Every custom image uploaded for this movie, across all three types (CIN-14535).
 *
 * The admin gallery does not need this — the TMDB image inventory already reports each
 * upload as an entry with `isCustomUpload` — but the upload modal does, to list what the
 * movie already holds before anything is added.
 */
export async function getMovieImageUploads(movieId: MovieId): Promise<MovieImageUploadDto[] | null> {
    return cineamoApiClient
        .get<MovieImageUploadDto[]>(`/movies/${movieId}/image-uploads`, { credentials: 'same-origin' })
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * Attaches an already-uploaded asset to the movie as a custom image of one type.
 *
 * Adding an upload shows it to nobody by itself: it becomes visible to viewers only once
 * a locale's primary pick names it, which is a separate PATCH on the movie.
 */
export async function createMovieImageUpload(
    movieId: MovieId,
    type: MovieImageUploadDto['type'],
    assetRef: string
): Promise<MovieImageUploadDto | null> {
    return cineamoApiClient
        .post<MovieImageUploadDto>(`/movies/${movieId}/image-uploads`, { type, assetRef })
        .then((response) => response.data)
        .catch(() => null);
}

/**
 * Deletes ONE upload. The movie's other uploads survive, and so do their picks — only the
 * locales that pointed at this one are cleared, and those fall back to the automatic pick.
 */
export async function deleteMovieImageUpload(movieId: MovieId, assetRef: string): Promise<boolean> {
    return cineamoApiClient
        .delete(`/movies/${movieId}/image-uploads/${assetRef}`, { credentials: 'same-origin' })
        .then(() => true)
        .catch(() => false);
}

/**
 * Copies a movie into a movie of ours with its own id, detached from TMDB (CIN-14766).
 *
 * The copy has no Cineamo Id, no TMDB id and no IMDB id, is excluded from the TMDB sync and starts
 * without a Rentrak Title. Age ratings, trailers, versions, accessibility, configuration, custom
 * image uploads and showings stay with the movie it was copied from. Admin only.
 */
export async function cloneMovie(movieId: MovieId): Promise<MovieDto | null> {
    return cineamoApiClient
        .post<MovieDto>(`/movies/${movieId}/clone`)
        .then((response) => response.data)
        .catch(() => null);
}

export async function updateMovie(movieId: MovieId, data: MovieDtoInput): Promise<ApiResponse<MovieDto> | null> {
    return cineamoApiClient
        .patch<ApiResponse<MovieDto>>(`/movies/${movieId}`, data)
        .then((response) => response.data)
        .catch(() => null);
}
